import { useQueryClient } from "@tanstack/react-query";
import {
  type User as FirebaseUser,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updatePassword,
} from "firebase/auth";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { setTokenProvider } from "@/api/fetchApi";
import { useClientConfig } from "@/api/queries";
import { initFirebase } from "./firebase";

export type AuthState = {
  /** False until Firebase has reported the first auth state. */
  ready: boolean;
  user: FirebaseUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerification: () => Promise<void>;
  /** Re-reads the Firebase user (e.g. after the email link was opened). */
  reload: () => Promise<FirebaseUser | null>;
  changePassword: (current: string, next: string) => Promise<void>;
  /** GET /api/config failed and nothing is cached: sign-in can't work right now. */
  unavailable?: boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const configQuery = useClientConfig();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [ready, setReady] = useState(false);
  const config = configQuery.data;
  const auth = useMemo(() => (config ? initFirebase(config) : null), [config]);

  useEffect(() => {
    if (!auth) return;
    setTokenProvider(async () => (auth.currentUser ? auth.currentUser.getIdToken() : null));
    return onAuthStateChanged(auth, (next) => {
      setUser((prev) => {
        if (prev && (!next || next.uid !== prev.uid)) {
          // Another account (or signed out): nothing cached may leak across users.
          queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "config" });
        }
        return next;
      });
      setReady(true);
    });
  }, [auth, queryClient]);

  const requireAuth = useCallback(() => {
    if (!auth) throw new Error("Sign-in isn't available yet. Try again in a moment.");
    return auth;
  }, [auth]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(requireAuth(), email, password);
      },
      signUp: async (email, password) => {
        const cred = await createUserWithEmailAndPassword(requireAuth(), email, password);
        if (config?.requireEmailVerification) {
          await sendEmailVerification(cred.user).catch(() => undefined);
        }
      },
      signOut: async () => {
        await fbSignOut(requireAuth());
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "config" });
      },
      resetPassword: async (email) => {
        await sendPasswordResetEmail(requireAuth(), email);
      },
      sendVerification: async () => {
        const current = requireAuth().currentUser;
        if (current) await sendEmailVerification(current);
      },
      reload: async () => {
        const current = requireAuth().currentUser;
        if (!current) return null;
        await current.reload();
        await current.getIdToken(true);
        setUser(requireAuth().currentUser);
        return requireAuth().currentUser;
      },
      changePassword: async (currentPassword, next) => {
        const current = requireAuth().currentUser;
        if (!current?.email) throw new Error("You're not signed in.");
        await reauthenticateWithCredential(
          current,
          EmailAuthProvider.credential(current.email, currentPassword),
        );
        await updatePassword(current, next);
      },
    }),
    [ready, user, requireAuth, config?.requireEmailVerification, queryClient],
  );

  // Without GET /api/config there is no Firebase, so nobody can be signed in. Public pages still
  // render; the routes that need an account show the boot error (RequireAuth).
  const unavailable = configQuery.isError && !config;
  const state = useMemo(
    () =>
      unavailable ? { ...value, ready: true, user: null, unavailable } : { ...value, unavailable },
    [value, unavailable],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

/**
 * A fixed auth state, for tests and the dev-only page gallery (src/dev). Never used by the app's
 * routes: real sessions always come from AuthProvider.
 */
export function StaticAuthProvider({
  value,
  children,
}: {
  value: Partial<AuthState> & { user: FirebaseUser | null };
  children: ReactNode;
}) {
  const noop = async () => undefined;
  const state: AuthState = {
    ready: true,
    signIn: noop,
    signUp: noop,
    signOut: noop,
    resetPassword: noop,
    sendVerification: noop,
    reload: async () => value.user,
    changePassword: noop,
    ...value,
  };
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Firebase error codes to the sentences we show. */
export function authErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-login-credentials":
      return "That email and password don't match an account.";
    case "auth/email-already-in-use":
      return "There's already an account with that email. Sign in instead.";
    case "auth/weak-password":
      return "Choose a password with at least 6 characters.";
    case "auth/invalid-email":
      return "That doesn't look like an email address.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a minute and try again.";
    case "auth/network-request-failed":
      return "We couldn't reach the sign-in service. Check your connection.";
    case "auth/requires-recent-login":
      return "For your security, sign in again before changing this.";
    default:
      return error instanceof Error ? error.message : "Something went wrong. Try again.";
  }
}
